'use client';

import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { readCartMessage } from './cartMessages';
import CartSummaryContent from './CartSummaryContent';

export default function CartSubtotalOffcanvas({ children, ...props }) {
  const [isFooterOverlap, setIsFooterOverlap] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('subtotal-offcanvas-active');

    const setFooterOverlapState = (nextValue) => {
      setIsFooterOverlap((current) => {
        if (current === nextValue) {
          return current;
        }

        return nextValue;
      });
      root.classList.toggle('subtotal-offcanvas-scrolling', nextValue);
    };

    const setHeight = () => {
      const el = document.querySelector('.subtotal-offcanvas.offcanvas-bottom');
      const height = el instanceof HTMLElement ? el.offsetHeight : 0;
      root.style.setProperty('--subtotal-offcanvas-height', `${height}px`);
    };

    const updateFooterOverlap = () => {
      const footerEl = document.querySelector('footer');
      const offcanvasEl = document.querySelector(
        '.subtotal-offcanvas.offcanvas-bottom',
      );

      if (
        !(footerEl instanceof HTMLElement) ||
        !(offcanvasEl instanceof HTMLElement)
      ) {
        setFooterOverlapState(false);
        return;
      }

      const footerRect = footerEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const overlapOffset = Math.min(offcanvasEl.offsetHeight, 160);
      const nextOverlap = footerRect.top <= viewportHeight - overlapOffset;

      setFooterOverlapState(nextOverlap);
    };

    const frame = requestAnimationFrame(() => {
      setHeight();
      updateFooterOverlap();
    });
    const observer = new ResizeObserver(setHeight);
    const el = document.querySelector('.subtotal-offcanvas.offcanvas-bottom');
    if (el instanceof HTMLElement) {
      observer.observe(el);
    }
    const footerEl = document.querySelector('footer');
    const footerObserver = new IntersectionObserver(
      () => {
        updateFooterOverlap();
      },
      {
        threshold: [0, 0.05, 0.1, 0.25, 0.5, 1],
      },
    );

    if (footerEl instanceof HTMLElement) {
      footerObserver.observe(footerEl);
    }

    const handleViewportChange = () => {
      setHeight();
      updateFooterOverlap();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', updateFooterOverlap, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', updateFooterOverlap);
      observer.disconnect();
      footerObserver.disconnect();
      root.classList.remove('subtotal-offcanvas-active');
      root.classList.remove('subtotal-offcanvas-scrolling');
      root.style.removeProperty('--subtotal-offcanvas-height');
    };
  }, []);

  return (
    <Offcanvas
      className={`subtotal-offcanvas checkout-summary ${isFooterOverlap ? 'footer-overlap' : ''}`.trim()}
      placement="bottom"
      show
      backdrop={false}
      scroll
      aria-label={readCartMessage(
        props.messages,
        'cart',
        'subtotalCard.title',
        'Order summary',
      )}
    >
      <Offcanvas.Body className="subtotal-body">
        {children || (
          <CartSummaryContent {...props} couponClassName="coupon-inline" />
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
